import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertStatus, AlertList } from '@front-core/alert';
import { AvatarSize } from '@front-core/avatar';
import { Button, Size, Intent } from '@front-core/button';
import { FormField, FormFieldIndent } from '@front-core/form-field';
import { Textarea, TextareaSize } from '@front-core/textarea';
import { LabelLargeMedium, LabelSmallRegular } from '@front-core/typography';
import { memo, useState, Fragment } from 'react';
import { agentsSlice } from '@entities/agents';
import { reviewsApi, ReviewEventTypes } from '@entities/reviews';
import { AgentMetricHashes } from '@shared/config/metrics';
import CustomError from '@shared/lib/customError';
import { useAppDispatch } from '@shared/lib/hooks/useAppDispatch';
import { useAppSelector } from '@shared/lib/hooks/useAppSelector';
import { isInvalidField } from '@shared/lib/isInvalidField';
import { sendMetrics } from '@shared/lib/sendMetrics';
import InputRating from '@shared/ui/InputRating';
import LayoutAvatar from '@shared/ui/layout/avatar';
import { validate } from '../../../lib/validate';
import AgentReviewUpdateDeleteButton from '../deleteButton';
import styles from './agentReviewUpdateForm.scss';
const AgentReviewUpdateForm = ({ review, onCancel, onSubmit }) => {
    var _a, _b, _c, _d;
    const agent = useAppSelector(state => { var _a; return (_a = state.agents) === null || _a === void 0 ? void 0 : _a.agent.details; });
    const userId = useAppSelector(state => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.casId; });
    const regionId = useAppSelector(state => { var _a; return (_a = state.geo) === null || _a === void 0 ? void 0 : _a.region.guid; });
    const dispatch = useAppDispatch();
    const [mutateReview] = reviewsApi.useUpdateReviewMutation();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [alertList, setAlertList] = useState([]);
    const hasDate = Boolean(review.dealIssuedAt);
    const isCurrentUser = userId === ((_a = review.author) === null || _a === void 0 ? void 0 : _a.id);
    const createReview = async (data) => {
        var _a;
        const body = getData(data);
        setIsLoading(true);
        try {
            validate(body);
            const response = await mutateReview(body);
            if ('data' in response) {
                dispatch(agentsSlice.finishAgentCreateReview());
                onSubmit();
                setIsLoading(false);
                sendMetrics({
                    eventType: ReviewEventTypes.ReviewChanged,
                    elementId: AgentMetricHashes.SuccessResponseReview,
                    eventSense: {
                        agentCasId: agent === null || agent === void 0 ? void 0 : agent.casId,
                        regionId,
                        reviewId: review.id
                    }
                });
            }
            if ('error' in response && response.error) {
                const responseError = response.error;
                const errorMessage = 'status' in responseError ? (_a = responseError.data.errors[0]) === null || _a === void 0 ? void 0 : _a.message : '';
                setIsLoading(false);
                setAlertList([{
                        id: '1',
                        showCloseIcon: true,
                        title: errorMessage,
                        duration: 4,
                        status: AlertStatus.ERROR
                    }]);
            }
        }
        catch (error) {
            if (error instanceof CustomError) {
                const customError = error;
                setErrors(prevState => ({
                    ...prevState,
                    [customError.name]: customError.message
                }));
                setIsLoading(false);
            }
        }
    };
    const getData = (data) => {
        const formattedData = {
            reviewId: review.id
        };
        for (const [key, value] of data) {
            const hasValue = typeof value === 'string' && value.length;
            if (hasValue) {
                formattedData[key] = value.trim();
            }
            else {
                formattedData[key] = null;
            }
        }
        return formattedData;
    };
    const resetErrors = (error) => {
        setErrors(prevState => {
            if (prevState && error in prevState) {
                delete prevState[error];
            }
            return {
                ...prevState
            };
        });
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        createReview(data).then();
    };
    return _jsxs(Fragment, { children: [_jsxs("form", { className: styles.form, onSubmit: handleSubmit, children: [_jsxs("div", { className: styles.userContainer, children: [_jsx(LayoutAvatar, { size: AvatarSize.Small, name: ((_b = review.author) === null || _b === void 0 ? void 0 : _b.name) || undefined, imageSrc: ((_c = review.author) === null || _c === void 0 ? void 0 : _c.avatarUrl) || undefined, "data-sentry-element": "LayoutAvatar", "data-sentry-source-file": "index.tsx" }), _jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.userTitle, children: ((_d = review.author) === null || _d === void 0 ? void 0 : _d.name) ? _jsx(LabelLargeMedium, { tag: "strong", wrapper: false, className: styles.userTitleText, children: review.author.name }) : null }), hasDate ? _jsxs(LabelSmallRegular, { tag: "span", wrapper: false, className: styles.secondaryText, children: ["\u0421\u0434\u0435\u043B\u043A\u0430 \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0430 ", review.dealIssuedAt] }) : null] })] }), _jsx(InputRating, { defaultValue: review.rating, name: "rating", "data-sentry-element": "InputRating", "data-sentry-source-file": "index.tsx" }), _jsx(FormField, { fluid: true, indent: FormFieldIndent.None, errorMessage: errors === null || errors === void 0 ? void 0 : errors.message, validity: isInvalidField(errors === null || errors === void 0 ? void 0 : errors.message), "data-sentry-element": "FormField", "data-sentry-source-file": "index.tsx", children: _jsx(Textarea, { autoFocus: true, name: "message", rows: 4, placeholder: "\u041D\u0430\u043F\u0438\u0448\u0438\u0442\u0435, \u043A\u0430\u043A \u043F\u0440\u043E\u0448\u043B\u043E \u0441\u043E\u0442\u0440\u0443\u0434\u043D\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0441 \u0440\u0438\u0435\u043B\u0442\u043E\u0440\u043E\u043C. \u0420\u0430\u0441\u0441\u043A\u0430\u0436\u0438\u0442\u0435, \u0447\u0442\u043E \u043F\u043E\u043D\u0440\u0430\u0432\u0438\u043B\u043E\u0441\u044C, \u0430 \u0447\u0442\u043E \u043D\u0435\u0442.", size: TextareaSize.Medium, showClearIcon: false, onFocus: () => resetErrors('message'), defaultValue: review === null || review === void 0 ? void 0 : review.message, "data-sentry-element": "Textarea", "data-sentry-source-file": "index.tsx" }) }), _jsxs("div", { className: styles.buttons, children: [isCurrentUser ? _jsx(AgentReviewUpdateDeleteButton, { reviewId: review.id }) : null, _jsx(Button, { size: Size.Medium, intent: Intent.Secondary, text: "\u041E\u0442\u043C\u0435\u043D\u0430", disabled: isLoading, onClick: onCancel, "data-sentry-element": "Button", "data-sentry-source-file": "index.tsx" }), _jsx(Button, { disabled: Boolean(errors === null || errors === void 0 ? void 0 : errors.message), loading: isLoading, type: "submit", size: Size.Medium, text: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C", "data-sentry-element": "Button", "data-sentry-source-file": "index.tsx" })] })] }), _jsx(AlertList, { list: alertList, onChangeAlertList: setAlertList, "data-sentry-element": "AlertList", "data-sentry-source-file": "index.tsx" })] });
};
export default memo(AgentReviewUpdateForm);
